import * as THREE from 'three';

/**
 * Mouse / touch orbit rig — drag to reframe, scroll to zoom.
 * Games call attach(dom) after canvas exists, then apply(camera, target) each frame.
 */
export class OrbitRig {
  azimuth = Math.PI / 4;
  elevation = Math.PI / 5;
  distance = 18;
  minDist = 8;
  maxDist = 40;
  minElev = 0.15;
  maxElev = 1.35;
  damping = 0.18;

  private targetAz = this.azimuth;
  private targetEl = this.elevation;
  private targetDist = this.distance;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private el: HTMLElement | null = null;
  private enabled = true;

  constructor(opts?: { azimuth?: number; elevation?: number; distance?: number }) {
    if (opts?.azimuth !== undefined) this.azimuth = this.targetAz = opts.azimuth;
    if (opts?.elevation !== undefined) this.elevation = this.targetEl = opts.elevation;
    if (opts?.distance !== undefined) this.distance = this.targetDist = opts.distance;
  }

  attach(el: HTMLElement): void {
    this.detach();
    this.el = el;
    el.style.touchAction = 'none';
    el.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    el.addEventListener('wheel', this.onWheel, { passive: false });
  }

  detach(): void {
    if (!this.el) return;
    this.el.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    this.el.removeEventListener('wheel', this.onWheel);
    this.el = null;
    this.dragging = false;
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.dragging = false;
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.enabled || e.button !== 0) return;
    // don't steal if hitting UI chrome outside canvas — canvas only
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    try {
      this.el?.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.dragging || !this.enabled) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.targetAz -= dx * 0.007;
    this.targetEl += dy * 0.005;
    this.targetEl = Math.max(this.minElev, Math.min(this.maxElev, this.targetEl));
  };

  private onUp = (): void => {
    this.dragging = false;
  };

  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    this.targetDist *= e.deltaY > 0 ? 1.08 : 0.92;
    this.targetDist = Math.max(this.minDist, Math.min(this.maxDist, this.targetDist));
  };

  /** Smooth toward targets and place camera looking at `lookAt`. */
  apply(camera: THREE.Camera, lookAt: THREE.Vector3, dt = 1 / 60): void {
    const k = 1 - Math.pow(1 - this.damping, dt * 60);
    this.azimuth += (this.targetAz - this.azimuth) * k;
    this.elevation += (this.targetEl - this.elevation) * k;
    this.distance += (this.targetDist - this.distance) * k;

    const cosE = Math.cos(this.elevation);
    const x = lookAt.x + this.distance * cosE * Math.sin(this.azimuth);
    const y = lookAt.y + this.distance * Math.sin(this.elevation);
    const z = lookAt.z + this.distance * cosE * Math.cos(this.azimuth);
    camera.position.set(x, y, z);
    camera.lookAt(lookAt);
  }
}
